import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  input,
  OnChanges,
  signal,
  SimpleChanges,
  WritableSignal,
  output
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { v4 as uuidv4 } from 'uuid'; // Se preferisci usare una libreria per generare ID univoci, come uuid
import { MultiSelectGroup, MultiSelectMode, MultiSelectOption } from './multiselect.model';

@Component({
    selector: 'lib-multi-select',
    imports: [CommonModule, MatFormFieldModule, MatIconModule, FormsModule, ReactiveFormsModule, MatSelectModule],
    templateUrl: './multiselect.component.html',
    styleUrls: ['./multiselect.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectComponent<T extends MultiSelectOption> implements OnChanges {
  label = input('');
  inputPlaceholder = input('Enter...');
  reset = input()
  disabled = input<boolean>(false);

  @Input() set multiSelectItems(value: (T | MultiSelectGroup<T>)[]) {
    const itemsWithId = value.map(item => {
      if ('options' in item) {
        return {
          ...item,
          id: uuidv4(),
          options: item.options.map(option => ({
            ...option,
            _id: uuidv4(),
          })),
        };
      } else {
        return {
          ...item,
          _id: uuidv4(),
        };
      }
    });

    this.selectList.set(itemsWithId);
    this.resetSelectedValues();

    //check type and set mode
    if (this.selectList().some(item => 'options' in item)) {
      this.mode.set('group');
    } else {
      this.mode.set('single');
    }
    this.setPreselectedOptions();
  }

  readonly selectionEmitter = output<T[]>();

  selectedValues: T[] = [];
  multiSelectItemsT = computed<T[]>(() => this.selectList() as T[]);
  multiSelectItemsGroup = computed<MultiSelectGroup<T>[]>(() => this.selectList() as MultiSelectGroup<T>[]);

  readonly selectList: WritableSignal<(T | MultiSelectGroup<T>)[]> = signal([]);
  inputSearchValue: WritableSignal<string> = signal('');
  readonly mode: WritableSignal<MultiSelectMode> = signal('single');
  readonly isDisabled = computed(() => {
    return !this.selectList().length;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reset']) {
      if (this.reset()) {
        this.onClickResetSelection()
      }
    }
  }

  onSelectionChange(event: MatOptionSelectionChange, item: MultiSelectOption) {
    if (event.isUserInput) {
      if (event.source.selected) {
        item.selected = true;
        this.selectedValues = [...this.selectedValues, event.source.value];
      } else {
        item.selected = false;
        this.selectedValues = this.selectedValues.filter(value => value !== event.source.value);
      }
      this.emitSelectedValues();
      this.resetInputSearch();
    }
  }

  onClickClearSearchInput() {
    this.resetInputSearch();
  }

  onClickResetSelection() {
    this.resetSelectedOptions();
    this.resetSelectedValues();
    this.resetInputSearch();
    this.emitSelectedValues();
  }

  filterOption(option: T, search: string): boolean {
    if (!search) return true;
    return option.label.toLowerCase().includes(search.toLocaleLowerCase());
  }

  anyVisibleOptions(): boolean {
    return !!document.querySelectorAll('mat-option:not(.hidden)').length;
  }

  private resetInputSearch() {
    this.inputSearchValue.set('');
  }

  private resetSelectedValues() {
    this.selectedValues = [];
  }

  private emitSelectedValues() {
    this.selectionEmitter.emit(this.selectedValues);
  }

  private resetSelectedOptions() {
    const unselectedItems =
      this.mode() === 'group'
        ? this.multiSelectItemsGroup().map(group => ({
            ...group,
            options: group.options.map(option => ({
              ...option,
              selected: false,
            })),
          })) || []
        : this.multiSelectItemsT().map(option => ({
            ...option,
            selected: false,
          })) || [];

    this.selectList.set(unselectedItems);
  }

  private setPreselectedOptions() {
    let selectedOptions: T[] = [];

    if (this.mode() === 'group') {
      selectedOptions = this.multiSelectItemsGroup().reduce(
        (acc, group) => [...acc, ...group.options.filter(option => option.selected)],
        [] as T[],
      );
    } else {
      selectedOptions = this.multiSelectItemsT().filter(option => option.selected);
    }

    this.selectedValues = [...this.selectedValues, ...selectedOptions];
    this.emitSelectedValues();
  }

  private filterGroups(groups: MultiSelectGroup<T>[], search: string): MultiSelectGroup<T>[] {
    return groups.filter(group => {
      group.options = group.options.filter(option => this.filterOption(option, search));
      return group.options.length;
    });
  }

  //filter for groups
  /* private filterMultiSelectItems(
    optionsList: (T | MultiSelectGroup<T>)[] = [],
    search: string,
  ): (T | MultiSelectGroup<T>)[] {
    return this.mode() === 'group'
      ? this.filterGroups(optionsList as MultiSelectGroup<T>[], search)
      : optionsList.filter(option => this.filterOption(option as T, search));
  } */
}
